import { createFileRoute } from '@tanstack/react-router'
import { Card, CardContent, CardHeader, CardTitle, PageHeader, Badge } from '@/components/ui'
import { useQuery } from '@tanstack/react-query'
import { adminApi } from '@/lib/api'
import { DollarSign, FileText, AlertCircle } from 'lucide-react'

export const Route = createFileRoute('/_app/billing')({
  component: BillingPage,
})

interface TenantRich {
  id: string
  name: string
  slug: string
  plan: string
  status: string
}

function BillingPage() {
  const { data } = useQuery({
    queryKey: ['tenants-rich'],
    queryFn: () => adminApi.get<{ data: TenantRich[] }>('/api/admin/tenants-rich'),
  })

  return (
    <>
      <PageHeader
        title="Billing"
        description="Suscripciones y cobros a tenants"
      />
      <div className="p-8 space-y-6">
        <Card className="bg-yellow-50 dark:bg-yellow-900/10 border-yellow-200 dark:border-yellow-800">
          <CardContent className="p-4 flex items-start gap-3">
            <AlertCircle className="size-5 text-yellow-700 dark:text-yellow-400 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-yellow-700 dark:text-yellow-400 font-medium">Stripe Billing pendiente de integración</p>
              <p className="text-xs text-foreground mt-1">
                Cuando se integre Stripe Billing + Meters API, esta pantalla muestra: invoices por tenant,
                MRR, churn, overdue, cobros automáticos. Por ahora solo lista qué plan tiene cada tenant.
              </p>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">MRR</p>
                  <p className="text-3xl font-bold mt-2 text-muted-foreground">$0</p>
                  <p className="text-xs text-muted-foreground mt-1">tras Stripe Billing</p>
                </div>
                <DollarSign className="size-7 text-emerald-700 dark:text-emerald-400" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Invoices este mes</p>
                  <p className="text-3xl font-bold mt-2 text-muted-foreground">0</p>
                  <p className="text-xs text-muted-foreground mt-1">facturadas</p>
                </div>
                <FileText className="size-7 text-blue-700 dark:text-blue-400" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Past due</p>
                  <p className="text-3xl font-bold mt-2 text-muted-foreground">0</p>
                  <p className="text-xs text-muted-foreground mt-1">tenants con cobro pendiente</p>
                </div>
                <AlertCircle className="size-7 text-yellow-700 dark:text-yellow-400" />
              </div>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Planes activos por tenant</CardTitle>
          </CardHeader>
          <CardContent>
            {!data?.data.length ? (
              <p className="text-sm text-muted-foreground">Sin tenants.</p>
            ) : (
              <div className="space-y-2">
                {data.data.map((t) => (
                  <div key={t.id} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                    <div>
                      <p className="font-medium text-sm">{t.name}</p>
                      <p className="text-xs text-muted-foreground font-mono">{t.slug}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="muted">{t.plan}</Badge>
                      <Badge variant={t.status === 'active' ? 'success' : 'warning'}>{t.status}</Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  )
}
